import { FC, useState, useEffect } from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Grid,
  FormControl,
  TextField,
  FormControlLabel,
  Switch,
} from "@material-ui/core";

import {
  OpenEdgePaymentData,
  ErrorProps,
} from "../../../entities/PaymentOptions";
import ReactNotification, {
  SUCCESS,
  DANGER,
} from "../../../utils/ReactNotification.config";

import { api, base_url } from "../../../utils/env";
import { useStylesInput } from "./style";
import {
  check,
  CYBERSOURCE,
  CYBERSOURCE_DISABLED_MESSAGE,
  CYBERSOURCE_ENABLED_MESSAGE,
  CYBERSOURCE_TITLE,
  CybersourcePaymentDataErrorProps,
  DETAILS_INVALID,
  MERCHANT_ID,
  MERCHANT_ID_MANDATORY,
  objError,
} from "./paymentMethods.const";
import { PaymentMode } from "../../../constants/payment.const";

interface OpenEdgeMethodsProps {
  type: string | null;
  companyID: string | null;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  data: any;
  masterID: string | null;
}

const CybersourceSelection: FC<OpenEdgeMethodsProps> = ({
  type,
  companyID,
  setLoading,
  data,
  masterID,
}: OpenEdgeMethodsProps) => {
  const [paymentData, setPaymentData] = useState<OpenEdgePaymentData>(check);
  const [errors, setErrors] = useState<CybersourcePaymentDataErrorProps>(
    objError
  );
  const classesInput = useStylesInput();
  useEffect(() => {
    if (companyID && companyID !== "default") {
      setPaymentData({
        ...paymentData,
        ...check,
        ...data,
        merchantId:
          data && data?.paymentGateway === "Cybersource" && data?.merchantId,
        masterId: Number(masterID),
        companyId: companyID,
      });
    } else {
      setPaymentData({
        ...paymentData,
        ...check,
        masterId: Number(masterID),
        companyId: companyID,
      });
    }
    setErrors(objError);
  }, [companyID, data, masterID]);

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    inputName: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    validation(inputName, event);
    setPaymentData({
      ...paymentData,
      ...{ [inputName]: event.currentTarget.value },
    });
  };

  const setError = (
    property: string = "",
    status: boolean = false,
    message: string = ""
  ) => {
    setErrors({
      ...errors,
      [property]: {
        status: status,
        message: message,
      },
    });
  };

  const validation = (
    property: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const value = event.currentTarget.value.trim();

    if (property === MERCHANT_ID) {
      if (!value) setError(property, true, MERCHANT_ID_MANDATORY);
      else setError(property);
    }
  };

  const onConfirmValidation = () => {
    let merchantIdError: ErrorProps;
    let valid: boolean = false;

    if (!paymentData.merchantId) {
      merchantIdError = {
        status: true,
        message: MERCHANT_ID_MANDATORY,
      };
      valid = true;
    } else {
      merchantIdError = {
        status: false,
        message: "",
      };
    }

    setErrors({
      merchantId: merchantIdError,
    });
    return valid;
  };

  const updatePaymentDataStatus = (status: string) => {
    setPaymentData({
      ...paymentData,
      ...{ paymentGateway: status },
      ...{ enable: false },
    });
  };

  const updatePaymentDataStatusAndAuthKey = (
    status: string,
    newAuthKey: string
  ) => {
    setPaymentData({
      ...paymentData,
      ...{
        paymentGateway: status,
        apiKey: newAuthKey,
      },
    });
  };

  const savePaymentOptions = async (statusCode: string) => {
    setLoading(true);
    const paylod = {
      companyId: paymentData.companyId?.toString().trim(),
      masterId: masterID,
      enable: false,
      authKey: paymentData.authKey?.toString().trim(),
      terminalId: paymentData.terminalId?.toString().trim(),
      xwebId: paymentData.xWebId?.toString().trim(),
      webToken: "",
      apiKey: "",
      merchantId: paymentData.merchantId?.toString().trim(),
      termsAndConditions: "",
      paymentGateway: statusCode,
    };
    const data = JSON.stringify(paylod);
    const customerServiceLinkEndUrl = `${base_url}${api}/cybersouce/create`;
    console.log(
      "Send cybersource payload to customerServiceLik " +
        customerServiceLinkEndUrl
    );
    await axios
      .post(customerServiceLinkEndUrl, data, {
        headers: {
          "content-type": "application/json",
        },
      })
      .then((response: AxiosResponse<any>) => {
        const TITLE: string = CYBERSOURCE_TITLE;
        let message: string;
        message = CYBERSOURCE_ENABLED_MESSAGE;
        if (response?.data?.paymentGateway === "Cybersource") {
          updatePaymentDataStatusAndAuthKey(
            PaymentMode.OPEN_EDGE,
            response?.data?.apiKey
          );
        } else {
          message = CYBERSOURCE_DISABLED_MESSAGE;
          updatePaymentDataStatusAndAuthKey(PaymentMode.OPEN_EDGE, "");
        }
        setLoading(false);
        ReactNotification.showNotification(TITLE, message, SUCCESS);
        window.location.replace(`/admin/successwarepay/`);
      })
      .catch((error: AxiosError<any>) => {
        console.error(
          "failed to send to cybersource endpoint" + customerServiceLinkEndUrl
        );
        console.error("error message " + error);
        setLoading(false);
        const TITLE: string = CYBERSOURCE;
        ReactNotification.showNotification(TITLE, DETAILS_INVALID, DANGER);

        if (statusCode === PaymentMode.CYBERSOURCE) {
          updatePaymentDataStatusAndAuthKey(PaymentMode.OPEN_EDGE, "");
        } else {
          updatePaymentDataStatus(PaymentMode.OPEN_EDGE);
        }
      });
  };

  const onConfirmAction = (status: string) => {
    onConfirmValidation()
      ? updatePaymentDataStatus(status)
      : savePaymentOptions(status);
  };

  const checkCybersourceDisable = (): boolean => {
    return paymentData?.paymentGateway !== "Cybersource" &&
      companyID &&
      companyID.trim() !== "" &&
      companyID !== "default"
      ? false
      : true;
  };

  return (
    <section className=" box-shadow custom-card-enable-success-pay">
      <Grid container direction="row">
        <span className="payment-options-title-label">
          {CYBERSOURCE_TITLE} Settings
        </span>
      </Grid>
      <div className="grid-margin">
        <Grid container direction="row" md={6} xs={12}>
          <div className="required-star">
            <FormControl
              required
              className={classesInput.formControl}
              size="small"
            >
              <TextField
                id={`x-web-${type}`}
                label="Organization ID"
                name={`xWebId-${type}`}
                variant="outlined"
                size="small"
                autoComplete="off"
                inputProps={{ className: classesInput.input }}
                error={errors.merchantId.status}
                value={paymentData?.merchantId ? paymentData?.merchantId : ""}
                helperText={errors.merchantId.message}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLInputElement | HTMLTextAreaElement
                  >
                ) => onInputChange(event, MERCHANT_ID)}
                onBlur={(event: any) => validation(MERCHANT_ID, event)}
                disabled={checkCybersourceDisable()}
              />
            </FormControl>
            <div className={`required-default`}>*</div>
          </div>
        </Grid>
        <Grid container md={12}>
          <Grid
            container
            direction="row"
            md={6}
            xs={12}
            alignItems="center"
            justifyContent="center"
          >
            <FormControlLabel
              className={classesInput.switch}
              classes={{ label: classesInput.switchLabel }}
              control={
                <Switch
                  className="custom-switch"
                  disabled={
                    companyID &&
                    companyID.trim() !== "" &&
                    companyID !== "default"
                      ? false
                      : true
                  }
                  checked={paymentData.paymentGateway === "Cybersource"}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onConfirmAction(
                      paymentData?.paymentGateway !== "Cybersource"
                        ? PaymentMode.CYBERSOURCE
                        : PaymentMode.OPEN_EDGE
                    )
                  }
                  name="checkedA-switch"
                />
              }
              label={`Enable ${CYBERSOURCE_TITLE} Access`}
            />
          </Grid>
        </Grid>
      </div>
    </section>
  );
};

export default CybersourceSelection;
